import React from 'react';
import { RESPIRATORY_MODES, SECRETION_AMOUNTS } from '../../../../../dtos/patientmr/charting/respiratory.dto';
import { SEIZURE_INTERVENTIONS } from '../../../../../dtos/patientmr/charting/seizure.dto';
import { OUTPUT_TYPES } from '../../../../../dtos/patientmr/charting/output.dto';
import type { Entry, OutputFormData, RespiratoryFormData, SeizureFormData } from '../../types';
import { clockLabel, codeLabel } from '../../model/entryMeta';
import { CheckboxGroup, Field, FieldsProps, InputField, inputCls, Segmented, SelectField, TextAreaField } from './fieldParts';

export const RESPIRATORY_MODE_LABELS: Record<string, string> = {
  VENT: 'Ventilator',
  BIPAP: 'BiPAP',
  CPAP: 'CPAP',
  O2: 'Oxygen',
  TRACH_COLLAR: 'Trach collar',
  ROOM_AIR: 'Room air',
};
export const SECRETION_LABELS: Record<string, string> = { NONE: 'None', SMALL: 'Small', MOD: 'Moderate', LARGE: 'Large' };
export const INTERVENTION_LABELS: Record<string, string> = {
  REPOSITION: 'Repositioned',
  O2: 'Oxygen',
  SUCTION: 'Suctioned',
  RESCUE_MED: 'Rescue medication',
  VNS: 'VNS magnet',
  EMS: 'EMS called',
};

/** Phase 18 M8 RESPIRATORY: mode first; vent settings only on a ventilator, BiPAP or CPAP */
export const RespiratoryFields: React.FC<FieldsProps<RespiratoryFormData>> = ({ value, onChange, errors, showRequired, isMobile, disabled }) => {
  const set = (field: keyof RespiratoryFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-cyan-700' };
  const vented = value.mode === 'VENT' || value.mode === 'BIPAP' || value.mode === 'CPAP';
  return (
    <div className="space-y-2">
      <Segmented name="resp-mode" label="Mode" required isMobile={isMobile} disabled={disabled}
        options={RESPIRATORY_MODES.map((m) => ({ value: m, label: RESPIRATORY_MODE_LABELS[m] }))}
        value={value.mode} onChange={set('mode')} error={errors.mode} showRequired={showRequired} />
      {vented && (
        <div className={`grid gap-1.5 ${isMobile ? 'grid-cols-2' : 'grid-cols-6'}`} data-testid="resp-vent-settings">
          <InputField {...common} id="resp-vent-mode" label="Vent mode" placeholder="e.g. SIMV" value={value.ventMode} onChange={set('ventMode')} />
          <InputField {...common} id="resp-rate" label="Rate" type="number" value={value.rate} onChange={set('rate')} error={errors.rate} />
          <InputField {...common} id="resp-tv" label="Tidal vol. mL" type="number" value={value.tidalVolumeMl} onChange={set('tidalVolumeMl')} error={errors.tidalVolumeMl} />
          <InputField {...common} id="resp-pip" label="PIP" type="number" value={value.pip} onChange={set('pip')} error={errors.pip} />
          <InputField {...common} id="resp-peep" label="PEEP" type="number" value={value.peep} onChange={set('peep')} error={errors.peep} />
          <InputField {...common} id="resp-fio2" label="FiO2 %" type="number" placeholder="21–100" value={value.fio2} onChange={set('fio2')} error={errors.fio2} />
        </div>
      )}
      <div className={`grid gap-1.5 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
        <InputField {...common} id="resp-o2flow" label="O2 flow L/min" type="number" value={value.o2FlowLpm} onChange={set('o2FlowLpm')} error={errors.o2FlowLpm} />
        <InputField {...common} id="resp-spo2" label="SpO2 %" type="number" value={value.spo2} onChange={set('spo2')} error={errors.spo2} />
        <InputField {...common} id="resp-suction" label="Suction count" type="number" inputMode="numeric" value={value.suctionCount} onChange={set('suctionCount')} error={errors.suctionCount} />
        <SelectField {...common} id="resp-secretions" label="Secretions"
          options={SECRETION_AMOUNTS.map((a) => ({ value: a, label: SECRETION_LABELS[a] }))}
          value={value.secretionAmount} onChange={set('secretionAmount')} />
        <InputField {...common} id="resp-secretion-color" label="Secretion color" placeholder="e.g. white, thick" value={value.secretionColor} onChange={set('secretionColor')} />
        <InputField {...common} id="resp-tolerance" label="Tolerance" placeholder="e.g. tolerated well" className={isMobile ? '' : 'col-span-3'} value={value.tolerance} onChange={set('tolerance')} />
      </div>
      <div className="flex flex-wrap gap-x-4">
        {(['alarmsChecked', 'circuitChecked'] as const).map((k) => (
          <label key={k} className="flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm text-gray-700">
            <input type="checkbox" className="h-5 w-5 rounded border-gray-300" disabled={disabled}
              checked={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.checked })} />
            {k === 'alarmsChecked' ? 'Alarms checked' : 'Circuit checked'}
          </label>
        ))}
      </div>
      <TextAreaField {...common} id="resp-notes" label="Notes" rows={2} value={value.notes} onChange={set('notes')} />
    </div>
  );
};

/** "Diastat 10 mg · 2:05 PM" for a MEDICATION entry */
const medEntryLabel = (e: Entry): string => {
  const d = e.data && typeof e.data === 'object' ? (e.data as { medicationName?: string; dose?: string }) : {};
  return [d.medicationName || 'Medication', d.dose].filter(Boolean).join(' ') + ` · ${clockLabel(e.observedAt || e.date)}`;
};

/** Phase 18 M8 SEIZURE: start time (the observed time), duration, interventions, rescue med link */
export const SeizureFields: React.FC<FieldsProps<SeizureFormData> & { medicationEntries: Entry[] }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, medicationEntries,
}) => {
  const set = (field: keyof SeizureFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-orange-700' };
  const showRescue = value.interventions.includes('RESCUE_MED') || Boolean(value.rescueMedEntryId);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1.5">
        <InputField {...common} id="seizure-started" label="Started at (observed at)" type="time" required
          value={value.startedTime} onChange={set('startedTime')} error={errors.startedAt} />
        <Field id="seizure-duration-min" label="Duration" labelClassName="text-orange-700" error={errors.durationSeconds} className="col-span-2">
          <div className="flex items-center gap-1">
            <input id="seizure-duration-min" aria-label="Duration minutes" type="number" inputMode="numeric" min={0} disabled={disabled}
              className={inputCls({ isMobile, disabled }, Boolean(errors.durationSeconds))} value={value.durationMinutes}
              onChange={(e) => onChange({ ...value, durationMinutes: e.target.value })} />
            <span className="text-xs text-gray-600">min</span>
            <input aria-label="Duration seconds" type="number" inputMode="numeric" min={0} max={59} disabled={disabled}
              className={inputCls({ isMobile, disabled }, Boolean(errors.durationSeconds))} value={value.durationSeconds}
              onChange={(e) => onChange({ ...value, durationSeconds: e.target.value })} />
            <span className="text-xs text-gray-600">s</span>
          </div>
        </Field>
      </div>
      <InputField {...common} id="seizure-type" label="Seizure type" placeholder="e.g. generalized tonic-clonic" value={value.seizureType} onChange={set('seizureType')} />
      <TextAreaField {...common} id="seizure-description" label="Description" rows={3} value={value.description} onChange={set('description')} />
      <CheckboxGroup name="seizure-interventions" label="Interventions" disabled={disabled}
        options={SEIZURE_INTERVENTIONS.map((i) => ({ value: i, label: INTERVENTION_LABELS[i] || codeLabel(i) }))}
        value={value.interventions} onChange={(interventions) => onChange({ ...value, interventions })} />
      {showRescue && (
        <SelectField {...common} id="seizure-rescue-med" label="Rescue medication (MAR entry this visit)"
          placeholder={medicationEntries.length ? 'Select the MAR entry…' : 'Chart the rescue medication in Meds first'}
          options={medicationEntries.map((e) => ({ value: String(e.id), label: medEntryLabel(e) }))}
          value={value.rescueMedEntryId} onChange={set('rescueMedEntryId')} error={errors.rescueMedEntryId} />
      )}
      <InputField {...common} id="seizure-post-ictal" label="Post-ictal state" placeholder="e.g. sleepy, back to baseline 20 min" value={value.postIctalState} onChange={set('postIctalState')} />
      <InputField {...common} id="seizure-notified" label="Notified (comma-separated)" placeholder="e.g. Mother, Dr. Lee" value={value.notified} onChange={set('notified')} />
      <TextAreaField {...common} id="seizure-notes" label="Notes" rows={2} value={value.notes} onChange={set('notes')} />
    </div>
  );
};

/** Phase 18 M8 OUTPUT */
export const OutputFields: React.FC<FieldsProps<OutputFormData>> = ({ value, onChange, errors, showRequired, isMobile, disabled }) => {
  const set = (field: keyof OutputFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-lime-700' };
  return (
    <div className="space-y-2">
      <Segmented name="output-type" label="Output" required isMobile={isMobile} disabled={disabled}
        options={OUTPUT_TYPES.map((t) => ({ value: t, label: codeLabel(t) }))}
        value={value.outputType} onChange={set('outputType')} error={errors.outputType} showRequired={showRequired} />
      <div className="grid grid-cols-2 gap-1.5">
        <InputField {...common} id="output-volume" label="Volume (mL)" type="number" value={value.volumeMl} onChange={set('volumeMl')} error={errors.volumeMl} />
        <InputField {...common} id="output-diapers" label="Diaper count" type="number" inputMode="numeric" value={value.diaperCount} onChange={set('diaperCount')} error={errors.diaperCount} />
      </div>
      <InputField {...common} id="output-characteristics" label="Characteristics" placeholder="e.g. yellow, clear; soft brown" value={value.characteristics} onChange={set('characteristics')} />
      <TextAreaField {...common} id="output-notes" label="Notes" rows={2} value={value.notes} onChange={set('notes')} />
    </div>
  );
};
